import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import ScrollAnimation from "../components/ScrollAnimation";

// Import graduation images
import graduationSaveDate1 from "../images/Graduation/ZU_2026_GRADUATION SAVE THE DATE Banner 1080 x 1080.png";
import graduationSaveDate2 from "../images/Graduation/ZU_2026_GRADUATION SAVE THE DATE Banner 1080 x 10802.png";
import graduationImportantDates1 from "../images/Graduation/ZU_2026_Graduation Web Slider Important Dates to note.png";
import graduationImportantDates2 from "../images/Graduation/ZU_2026_Graduation Web Slider Important Dates to note2.png";

const SLIDE_DURATION = 6000;

const GRADUATION_SLIDES = [
  {
    image: graduationSaveDate1,
    imageSet: null,
    badge: "Save the Date",
    title: "10th Graduation Ceremony 2026",
    description: "Join us as we celebrate the achievements of our graduates. Friday, 11 December 2026 at 7:00 AM EAT.",
    primaryButton: "Clearance Portal",
    primaryLink: "https://bit.ly/4C0oEXJ",
    secondaryButton: "Learn More",
    secondaryLink: "/graduation-countdown",
  },
  {
    image: graduationSaveDate2,
    imageSet: null,
    badge: "Save the Date",
    title: "10th Graduation Ceremony 2026",
    description: "A momentous occasion honoring academic excellence and innovation. Venue: Zetech University Technology Park Mang'u Campus.",
    primaryButton: "Clearance Portal",
    primaryLink: "https://bit.ly/4C0oEXJ",
    secondaryButton: "Learn More",
    secondaryLink: "/graduation-countdown",
  },
  {
    image: graduationImportantDates1,
    imageSet: null,
    badge: "Important Dates",
    title: "Graduation Timeline",
    description: "Mark your calendar with key dates leading up to the ceremony. Clearance, rehearsals, and more.",
    primaryButton: "Clearance Portal",
    primaryLink: "https://bit.ly/4C0oEXJ",
    secondaryButton: "View Timeline",
    secondaryLink: "/graduation-countdown",
  },
  {
    image: graduationImportantDates2,
    imageSet: null,
    badge: "Important Dates",
    title: "Prepare for Graduation",
    description: "Complete your clearance on time and ensure all requirements are met before the big day.",
    primaryButton: "Clearance Portal",
    primaryLink: "https://bit.ly/4C0oEXJ",
    secondaryButton: "View Timeline",
    secondaryLink: "/graduation-countdown",
  },
];

function HeroButton({ href, className, label }) {
  if (!label) return null;

  const link = (href || "").trim();
  const targetLink = link;

  if (!targetLink) {
    return null;
  }

  const isExternal = /^(https?:)?\/\//i.test(targetLink) ||
    /^(mailto:|tel:)/i.test(targetLink);

  if (isExternal) {
    return (
      <a
        href={targetLink}
        className={className}
        target={/^(mailto:|tel:)/i.test(targetLink) ? undefined : "_blank"}
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }

  if (targetLink.startsWith("#")) {
    return (
      <a href={targetLink} className={className}>
        {label}
      </a>
    );
  }

  return (
    <Link to={targetLink.startsWith("/") ? targetLink : `/${targetLink}`} className={className}>
      {label}
    </Link>
  );
}

export default function GraduationSlider({ onCycleEnd, onRequestVideo }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const reducedMotion = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const onRequestVideoRef = useRef(onRequestVideo);
  useEffect(() => {
    onRequestVideoRef.current = onRequestVideo;
  }, [onRequestVideo]);

  const onCycleEndRef = useRef(onCycleEnd);
  useEffect(() => {
    onCycleEndRef.current = onCycleEnd;
  }, [onCycleEnd]);

  const hoverCapable = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover)").matches
  );

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % GRADUATION_SLIDES.length);
  }, []);

  const prevSlide = useCallback(() => {
    if (current === 0 && onRequestVideoRef.current) {
      onRequestVideoRef.current();
      return;
    }
    setCurrent((prev) => (prev - 1 + GRADUATION_SLIDES.length) % GRADUATION_SLIDES.length);
  }, [current]);

  const touchStart = useRef(null);
  const handleTouchStart = (e) => {
    touchStart.current = e.touches?.[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStart.current;
    const delta = endX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(delta) < 45) return;
    if (delta < 0) nextSlide();
    else prevSlide();
  };

  useEffect(() => {
    if (paused || GRADUATION_SLIDES.length === 0) return undefined;

    const timer = setInterval(() => {
      setCurrent((prev) => {
        if (prev >= GRADUATION_SLIDES.length - 1) return prev;
        return prev + 1;
      });
    }, SLIDE_DURATION);

    return () => clearInterval(timer);
  }, [paused]);

  useEffect(() => {
    if (paused || GRADUATION_SLIDES.length === 0) return undefined;
    if (current < GRADUATION_SLIDES.length - 1) return undefined;

    const timer = setTimeout(() => {
      if (onCycleEndRef.current) {
        onCycleEndRef.current();
      } else {
        setCurrent(0);
      }
    }, SLIDE_DURATION);

    return () => clearTimeout(timer);
  }, [current, paused]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () =>
      window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  const activeSlide = GRADUATION_SLIDES[current] || GRADUATION_SLIDES[0];

  return (
    <section
      className="hero-slider graduation-slider"
      onMouseEnter={() => { if (hoverCapable.current) setPaused(true); }}
      onMouseLeave={() => { if (hoverCapable.current) setPaused(false); }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Zetech University Graduation 2026"
      data-reduced-motion={reducedMotion.current ? "true" : "false"}
    >
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={activeSlide.image}
          fetchPriority="high"
        />
      </Helmet>

      <picture>
        <img
          src={activeSlide.image}
          alt={activeSlide.title}
          className="hero-image hero-image--kenburns"
          fetchPriority="high"
          decoding="async"
          loading="eager"
        />
      </picture>

      <div className="hero-overlay">
        <div className="hero-wrapper">
          <div className="hero-content">
            <ScrollAnimation animation="fade-down" delay={0.1} duration={0.6}>
              <span className="hero-badge">{activeSlide.badge}</span>
            </ScrollAnimation>

            <ScrollAnimation animation="fade-up" delay={0.2} duration={0.8}>
              <h1>{activeSlide.title}</h1>
            </ScrollAnimation>

            <ScrollAnimation animation="fade-up" delay={0.3} duration={0.8}>
              <p>{activeSlide.description}</p>
            </ScrollAnimation>

            <ScrollAnimation animation="fade-up" delay={0.4} duration={0.6}>
              <div className="hero-buttons">
                <HeroButton
                  href={activeSlide.primaryLink}
                  className="btn-primary shimmer-effect"
                  label={activeSlide.primaryButton}
                />
                <HeroButton
                  href={activeSlide.secondaryLink}
                  className="btn-secondary"
                  label={activeSlide.secondaryButton}
                />
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </div>

      <button
        className="hero-arrow1 hero-arrow-left1"
        onClick={prevSlide}
        aria-label="Previous Slide"
      >
        <FaArrowLeft />
      </button>

      <button
        className="hero-arrow1 hero-arrow-right1"
        onClick={nextSlide}
        aria-label="Next Slide"
      >
        <FaArrowRight />
      </button>

      <div className="hero-indicators" role="tablist" aria-label="Choose a graduation slide">
        {onRequestVideo && (
          <button
            type="button"
            className="indicator indicator--video"
            onClick={() => onRequestVideo()}
            aria-label="Play the intro video"
            title="Intro video"
          />
        )}

        {GRADUATION_SLIDES.map((slide, index) => (
          <button
            key={`${slide.title}-${index}`}
            type="button"
            role="tab"
            aria-selected={current === index}
            className={`indicator ${current === index ? "active" : ""}`}
            onClick={() => setCurrent(index)}
            aria-label={slide.title ? `Go to slide: ${slide.title}` : `Go to slide ${index + 1}`}
            title={slide.title || `Slide ${index + 1}`}
          >
            {current === index && !paused && (
              <span className="indicator-progress" key={`p-${current}`} />
            )}
          </button>
        ))}
      </div>

      <p className="hero-sr-status" aria-live="polite">
        {`Slide ${current + 1} of ${GRADUATION_SLIDES.length}${
          activeSlide.title ? `: ${activeSlide.title}` : ""
        }`}
      </p>
    </section>
  );
}
